import { useCallback, useMemo, useState } from "react";
import { CargoSchema, CargoForm, defaultCargoForm } from "../schemas/Cargos_cadastro/cargo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { CargoCompleto } from "../../domain/models/cargo_completo";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useCargosCadastroPagination } from "../hooks/Cargos_cadastro/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * useCargosCadastroViewModel
 */

export const useCargosCadastroViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    rh: { getCargoByIdUseCase, getCargoHabilidadesUseCase, getCargoTreinamentosUseCase, createCargoUseCase, updateCargoUseCase },
  } = useDependencyInjection();

  const location = useLocation();

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [selectedIdCargo, setSelectedIdCargo] = useState<number | null>();
  const [selectedCargo, setSelectedCargo] = useState<CargoCompleto>();

  const [currentTab, setCurrentTab] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [cargoHabilidades, setCargoHabilidades] = useState<any[]>();
  const [cargoTreinamentos, setCargoTreinamentos] = useState<any[]>();

  const [treinamentosSelectionModel, setTreinamentosSelectionModel] = useState<GridRowSelectionModel>();

  const [habilidadesSelectionModel, setHabilidadesSelectionModel] = useState<GridRowSelectionModel>();

  const pagination = useCargosCadastroPagination();

  const hookForm = useForm<CargoForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultCargoForm,
    resolver: zodResolver(CargoSchema),
  });

  const backOrNavigate = useBackOrNavigate();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  /**
   * Estados computados
   */

  const isCreated = useMemo(() => {
    return selectedCargo !== undefined && selectedCargo !== null;
  }, [selectedCargo]);

  /**
   * Funcoes de carregamento
   */

  const loadCargo = useCallback(
    async (idCargo?: number | null) => {
      try {
        const filteredIdCargo = evaluateParamValue(idCargo, selectedIdCargo);
        if (filteredIdCargo !== null) {
          const cargo = await getCargoByIdUseCase.getCargoById(filteredIdCargo);

          hookForm.setValue("ativo", cargo.ativo ? 1 : 0);
          hookForm.setValue("descricao", cargo.descricao ?? "");
          hookForm.setValue("atribuicoes", cargo.atribuicoes ?? "");
          hookForm.setValue("escolaridade", cargo.escolaridade ?? "");
          hookForm.setValue("experiencia", cargo.experiencia ?? "");
          hookForm.setValue("qualificacoes", cargo.qualificacoes ?? "");

          setSelectedCargo(cargo);
        } else {
          hookForm.reset();
        }
      } catch (e: unknown) {
        setSelectedCargo(undefined);
        hookForm.reset();
        throw e;
      }
    },
    [hookForm, selectedIdCargo, getCargoByIdUseCase],
  );

  const loadCargoTreinamentos = useCallback(
    async (funcState?: { idCargo?: number; page?: number; rowsPerPage?: number }) => {
      try {
        const filteredIdCargo = funcState?.idCargo ?? selectedIdCargo ?? null;
        const filteredPage = funcState?.page ?? pagination.treinamentos.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.treinamentos.rowsPerPage;

        await getCargoTreinamentosUseCase.getCargoTreinamentos(filteredIdCargo, filteredPage, filteredRowsPerPage);

        const treinamentos = getCargoTreinamentosUseCase.items.filter((x) => x.checked).map((x) => x.idTreinamento);

        hookForm.setValue(
          "treinamentos",
          treinamentos.map((x) => ({ idTreinamento: x })),
        );

        setTreinamentosSelectionModel({ type: "include", ids: new Set([...treinamentos]) });
        setCargoTreinamentos(getCargoTreinamentosUseCase.items);

        pagination.treinamentosSetter.setTotalItems(getCargoTreinamentosUseCase.totalItems);
      } catch (e: unknown) {
        setCargoTreinamentos([]);
        throw e;
      }
    },
    [hookForm, selectedIdCargo, pagination, getCargoTreinamentosUseCase],
  );

  const loadCargoHabilidades = useCallback(
    async (funcState?: { idCargo?: number; page?: number; rowsPerPage?: number }) => {
      try {
        const filteredIdCargo = funcState?.idCargo ?? selectedIdCargo ?? null;
        const filteredPage = funcState?.page ?? pagination.treinamentos.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.treinamentos.rowsPerPage;

        await getCargoHabilidadesUseCase.getCargoHabilidades(filteredIdCargo, filteredPage, filteredRowsPerPage);

        const habilidades = getCargoHabilidadesUseCase.items.filter((x) => x.checked).map((x) => x.idHabilidade);

        hookForm.setValue(
          "habilidades",
          habilidades.map((x) => ({ idHabilidade: x })),
        );

        setHabilidadesSelectionModel({ type: "include", ids: new Set([...habilidades]) });
        setCargoHabilidades(getCargoHabilidadesUseCase.items);

        pagination.habilidadesSetter.setTotalItems(getCargoHabilidadesUseCase.totalItems);
      } catch (e: unknown) {
        setCargoHabilidades([]);
        throw e;
      }
    },
    [hookForm, selectedIdCargo, pagination, getCargoHabilidadesUseCase],
  );

  /**
   * UseEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ? Number(locationId) : null;

      const jobs = [];

      setErrorMessage(undefined);

      setSelectedIdCargo(id);

      await loadCargo(id);

      jobs.push(loadCargoTreinamentos({ idCargo: id ?? undefined }));
      jobs.push(loadCargoHabilidades({ idCargo: id ?? undefined }));

      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar o cargo!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleTabChange = useCallback((_: React.SyntheticEvent, newTab: number) => {
    setCurrentTab(newTab);
  }, []);

  const handleSubmit = useCallback(
    async (form: CargoForm) => {
      if (!loading && !saving) {
        try {
          setSaving(true);
          setPageSaving(true);

          if (!selectedCargo) {
            const idCargo = await createCargoUseCase.createCargo(
              form.ativo,
              form.descricao,
              form.atribuicoes,
              form.escolaridade,
              form.experiencia,
              form.qualificacoes,
              form.treinamentos?.map((x) => x.idTreinamento) ?? [],
              form.habilidades?.map((x) => x.idHabilidade) ?? [],
            );
            setSelectedIdCargo(idCargo);
            await loadCargo(idCargo);

            pushInfoNotification("Sucesso", "Cargo criado com sucesso.");
          } else {
            await updateCargoUseCase.updateCargo(
              selectedCargo.idCargo,
              form.ativo,
              form.descricao,
              form.atribuicoes,
              form.escolaridade,
              form.experiencia,
              form.qualificacoes,
              form.treinamentos?.map((x) => x.idTreinamento) ?? [],
              form.habilidades?.map((x) => x.idHabilidade) ?? [],
            );
            await loadCargo();
            pushInfoNotification("Sucesso", "Cargo editado com sucesso.");
          }
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao salvar o cargo!"));
        } finally {
          setSaving(false);
          setPageSaving(false);
        }
      }
    },
    [
      loading,
      saving,
      selectedCargo,
      createCargoUseCase,
      updateCargoUseCase,
      loadCargo,
      setPageSaving,
      pushErrorNotification,
      pushInfoNotification,
    ],
  );

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];

      setLoading(true);
      setErrorMessage(undefined);

      const idCargo = location.state?.idCargo as number | undefined;

      if (idCargo) {
        await loadCargo();
      }

      jobs.push(loadCargoTreinamentos());
      jobs.push(loadCargoHabilidades());

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar o cargo!"));
    } finally {
      setLoading(false);
    }
  }, [location, loadCargo, loadCargoTreinamentos, loadCargoHabilidades, pushErrorNotification]);

  const handleBack = useCallback(() => {
    backOrNavigate("/Cargos_lista");
  }, [backOrNavigate]);

  const handleCargoTreinamentosPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useCargosCadastroPagination.getState().treinamentosSetter.setPage(page + 1);
        await loadCargoTreinamentos({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar os treinamentos do cargo!"));
      } finally {
        setLoading(false);
      }
    },
    [loadCargoTreinamentos, pushErrorNotification],
  );

  const handleCargoTreinamentosRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useCargosCadastroPagination.getState().treinamentosSetter.setPage(1);
        useCargosCadastroPagination.getState().treinamentosSetter.setRowsPerPage(rowsPerPage);
        await loadCargoTreinamentos({ rowsPerPage, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar os treinamentos do cargo!"));
      } finally {
        setLoading(false);
      }
    },
    [loadCargoTreinamentos, pushErrorNotification],
  );

  const handleCargoHabilidadesPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useCargosCadastroPagination.getState().habilidadesSetter.setPage(page + 1);
        await loadCargoHabilidades({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar as habilidades do cargo!"));
      } finally {
        setLoading(false);
      }
    },
    [loadCargoHabilidades, pushErrorNotification],
  );

  const handleCargoHabilidadesRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useCargosCadastroPagination.getState().habilidadesSetter.setPage(1);
        useCargosCadastroPagination.getState().habilidadesSetter.setRowsPerPage(rowsPerPage);
        await loadCargoHabilidades({ rowsPerPage, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar as habilidades do cargo!"));
      } finally {
        setLoading(false);
      }
    },
    [loadCargoHabilidades, pushErrorNotification],
  );

  const handleTreinamentosSelectionModelChange = useCallback(
    (selectionModels: GridRowSelectionModel) => {
      hookForm.setValue(
        "treinamentos",
        [...selectionModels.ids.values()].map((x) => ({ idTreinamento: typeof x === "number" ? x : Number(x) })),
      );
      setTreinamentosSelectionModel(selectionModels);
    },
    [hookForm],
  );

  const handlehabilidadesSelectionModelChange = useCallback(
    (selectionModels: GridRowSelectionModel) => {
      hookForm.setValue(
        "habilidades",
        [...selectionModels.ids.values()].map((x) => ({ idHabilidade: typeof x === "number" ? x : Number(x) })),
      );
      setHabilidadesSelectionModel(selectionModels);
    },
    [hookForm],
  );

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (selectedIdCargo) {
          changeHistory.handleOpenChangeHistory(selectedIdCargo, LogTipoTabela.CARGO, true);
        }
      }
    },
    [selectedIdCargo, changeHistory],
  );

  return {
    hookForm,
    currentTab,
    loading,
    saving,
    errorMessage,
    cargoHabilidades,
    cargoTreinamentos,
    selectedIdCargo,
    selectedCargo,
    treinamentosSelectionModel,
    habilidadesSelectionModel,
    isCreated,
    pagination,
    changeHistory,
    handleTabChange,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleReload,
    handleBack,
    handleCargoTreinamentosPageChange,
    handleCargoTreinamentosRowsPerPageChange,
    handleCargoHabilidadesPageChange,
    handleCargoHabilidadesRowsPerPageChange,
    handleTreinamentosSelectionModelChange,
    handlehabilidadesSelectionModelChange,
    handleOpenChangeHistory,
  };
};
